import React from "react";
import { PowerSkillTestData } from "../../typings/powerskill";
import { Card, CardContent } from "../UI/Card";
import { Sparkles, Target, Lightbulb, MessageCircle, Users, Smile, Rocket, CheckCircle2 } from "lucide-react";
import { formatBold } from "../../utils/powerskill";

interface PowerSkillAiRecommendationProps {
  testData: PowerSkillTestData;
}

const PowerSkillAiRecommendation: React.FC<PowerSkillAiRecommendationProps> = ({
  testData,
}) => {
  const aspects = [
    { id: 'a', title: "Mengatur Diri & Tanggung Jawab", content: testData.ai_feedback_a, icon: Target, color: "text-blue-600", bg: "bg-blue-50" },
    { id: 'b', title: "Cara Berpikir & Memecahkan Masalah", content: testData.ai_feedback_b, icon: Lightbulb, color: "text-amber-600", bg: "bg-amber-50" },
    { id: 'c', title: "Berkomunikasi dengan Baik", content: testData.ai_feedback_c, icon: MessageCircle, color: "text-green-600", bg: "bg-green-50" },
    { id: 'd', title: "Bekerja Sama & Menghargai Orang Lain", content: testData.ai_feedback_d, icon: Users, color: "text-purple-600", bg: "bg-purple-50" },
    { id: 'e', title: "Sikap Positif & Mau Belajar", content: testData.ai_feedback_e, icon: Smile, color: "text-pink-600", bg: "bg-pink-50" },
  ];

  return (
    <div className="space-y-6">
      {/* Opening Summary */}
      <Card className="border-orange-100 bg-orange-50/30 overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="bg-orange-100 p-3 rounded-2xl">
              <Sparkles className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-orange-900 mb-2">Halo! Ini Ringkasan Untukmu</h3>
              <p className="text-orange-800 leading-relaxed italic">
                "{testData.ai_summary}"
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback per Aspect */}
      <div className="grid gap-4">
        <h4 className="font-bold text-gray-700 px-1 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-green-500" />
          Ulasan Aspek PowerSkills
        </h4>
        {aspects.map((aspect) => (
          <Card key={aspect.id} className="border-gray-100 shadow-sm">
            <CardContent className="p-5">
              <div className="flex gap-4">
                <div className={`${aspect.bg} ${aspect.color} p-3 rounded-xl h-fit`}>
                  <aspect.icon className="w-5 h-5" />
                </div>
                <div className="space-y-2">
                  <h5 className={`font-bold ${aspect.color}`}>{aspect.title}</h5>
                  <div 
                    className="text-gray-600 text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: formatBold(aspect.content) }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Focus Area */}
      <Card className="border-indigo-100 bg-indigo-600 text-white shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="bg-white/20 p-3 rounded-2xl">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <div className="space-y-3">
              <h3 className="text-lg font-bold">Kesempatan Belajar Utama</h3>
              <div 
                className="text-indigo-50 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: formatBold(testData.ai_focus_area) }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Closing Message */}
      <div className="text-center p-6 italic text-gray-500 font-medium">
        {testData.ai_closing}
      </div>
    </div>
  );
};

export default PowerSkillAiRecommendation;
